import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
const SLOT_MINUTES = 30;
const DEFAULT_SLOT_PX = 36;
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function parseHHMMSS(s) {
    const [h, m] = s.split(":").map(Number);
    return { h: h || 0, m: m || 0 };
}
function ymd(d) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function fmtHourLabel(hour24) {
    if (hour24 === 0)
        return "12 AM";
    if (hour24 === 12)
        return "12 PM";
    if (hour24 < 12)
        return `${hour24} AM`;
    return `${hour24 - 12} PM`;
}
function fmtBlockTime(d) {
    return d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
}
export default function WeekGrid({ weekStart, awakeStart, awakeEnd, blocks, onCreate, onSelect, nowTick, slotPx = DEFAULT_SLOT_PX, }) {
    const { startMin, endMin, slots } = useMemo(() => {
        const a = parseHHMMSS(awakeStart);
        const b = parseHHMMSS(awakeEnd);
        const startM = a.h * 60 + a.m;
        const endM = b.h * 60 + b.m;
        const slotCount = Math.max(1, Math.floor((endM - startM) / SLOT_MINUTES));
        const slotArr = Array.from({ length: slotCount }, (_, i) => startM + i * SLOT_MINUTES);
        return { startMin: startM, endMin: endM, slots: slotArr };
    }, [awakeStart, awakeEnd]);
    // 7 day Date objects, Monday → Sunday
    const days = useMemo(() => {
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(weekStart);
            d.setDate(weekStart.getDate() + i);
            d.setHours(0, 0, 0, 0);
            return d;
        });
    }, [weekStart]);
    // Index blocks by day for fast lookup. Use *local* date — slicing the ISO string
    // gives the UTC date and misbuckets blocks created near midnight (e.g., 9 PM EDT
    // = 1 AM UTC the next day, which would land on the wrong column otherwise).
    const blocksByDay = useMemo(() => {
        const map = {};
        for (const b of blocks) {
            const key = ymd(new Date(b.start_at));
            (map[key] ||= []).push(b);
        }
        return map;
    }, [blocks]);
    const gridHeightPx = useMemo(() => slots.length * slotPx, [slots.length, slotPx]);
    function blockLayout(b) {
        const s = new Date(b.start_at);
        const e = new Date(b.end_at);
        const sMin = s.getHours() * 60 + s.getMinutes();
        const eMin = e.getHours() * 60 + e.getMinutes();
        const rawTop = ((sMin - startMin) / SLOT_MINUTES) * slotPx;
        const rawBottom = ((eMin - startMin) / SLOT_MINUTES) * slotPx;
        // Clamp into the visible window so off-window blocks don't render at
        // weird offsets and overlap onto adjacent rows.
        const top = Math.max(0, rawTop);
        const bottom = Math.min(gridHeightPx, rawBottom);
        const height = Math.max(slotPx, bottom - top);
        return { top, height };
    }
    function handleSlotClick(day, slotMin) {
        const start = new Date(day);
        start.setHours(Math.floor(slotMin / 60), slotMin % 60, 0, 0);
        // SchedulePanel drop mode — see CalendarGrid for full notes. The
        // panel handles the API call when it owns the click.
        if (window.__schedulePanelOpen) {
            window.dispatchEvent(new CustomEvent("schedule:drop", {
                detail: { start_at: start.toISOString() },
            }));
            return;
        }
        if (!onCreate)
            return;
        const end = new Date(start);
        end.setMinutes(start.getMinutes() + SLOT_MINUTES);
        onCreate(start, end);
    }
    const today = ymd(new Date());
    // Now-line top offset (px from top of the week body) — null if "now" sits
    // outside the visible awake window. nowTick triggers a recompute every 5
    // min via the parent's setInterval.
    void nowTick;
    const nowTopPx = (() => {
        const now = new Date();
        const nowMin = now.getHours() * 60 + now.getMinutes();
        if (nowMin < startMin || nowMin > endMin)
            return null;
        return ((nowMin - startMin) / SLOT_MINUTES) * slotPx;
    })();
    const nowLabel = new Date().toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
    });
    return (_jsxs("div", { className: "week-grid", children: [_jsxs("div", { className: "week-head", children: [_jsx("div", { className: "time-col-head" }), days.map((d) => {
                        const isToday = ymd(d) === today;
                        return (_jsxs("div", { className: `day-head ${isToday ? "today" : ""}`, children: [_jsx("div", { className: "day-label", children: DAY_LABELS[d.getDay() === 0 ? 6 : d.getDay() - 1] }), _jsx("div", { className: "day-date", children: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) })] }, d.toISOString()));
                    })] }), _jsxs("div", { className: "week-body", style: { height: slots.length * slotPx }, children: [_jsx("div", { className: "time-col", children: slots.map((m, i) => (_jsx("div", { className: `time-slot ${m % 60 === 0 ? "hour-mark" : ""}`, style: { height: slotPx }, children: m % 60 === 0 ? fmtHourLabel(Math.floor(m / 60)) : "" }, i))) }), days.map((day) => {
                        const dayBlocks = blocksByDay[ymd(day)] ?? [];
                        const isTodayCol = ymd(day) === today;
                        return (_jsxs("div", { className: "day-col", children: [slots.map((m, i) => (_jsx("div", { className: `day-slot ${m % 60 === 0 ? "hour-mark" : ""}`, style: { height: slotPx }, onClick: () => handleSlotClick(day, m) }, i))), dayBlocks.map((b) => {
                                    const { top, height } = blockLayout(b);
                                    return (_jsxs("div", { className: `block week-block block-${b.block_type} ${b.is_personal ? "block-personal" : ""} ${b.redacted ? "block-redacted" : ""} ${b.is_completed ? "block-completed" : ""} ${b.reviewed_at ? "block-reviewed" : ""}`, style: { top, height }, onClick: (e) => {
                                            e.stopPropagation();
                                            onSelect?.(b);
                                        }, title: b.title, children: [_jsxs("div", { className: "block-title-row", children: [_jsx("div", { className: "block-title", children: b.title }), _jsxs("div", { className: "block-badges", children: [b.recurring_pattern_id && (_jsx("span", { className: "block-badge", title: "Recurring meeting", children: "\u21BB" })), b.is_completed && (_jsx("span", { className: "block-badge block-badge-done", title: "Marked complete", children: "\u2713" }))] })] }), _jsx("div", { className: "block-time", children: fmtBlockTime(new Date(b.start_at)) }), b.project_name && (_jsx("div", { className: "block-project", title: b.project_name, children: b.project_name })), b.task_id && b.task_progress_percent != null && (_jsx("div", { className: "block-progress-row", children: _jsx("div", { className: "block-progress-track", children: _jsx("div", { className: "block-progress-fill", style: { width: `${b.task_progress_percent}%` } }) }) }))] }, b.id));
                                }), isTodayCol && nowTopPx !== null && (_jsx("div", { className: "now-line", style: { top: nowTopPx }, children: _jsx("span", { className: "now-line-label", children: nowLabel }) }))] }, day.toISOString()));
                    })] })] }));
}
